import { useState, useEffect } from 'react'
import PeptideVial  from './PeptideVial'
import CreamTube    from './CreamTube'
import PillBottle   from './PillBottle'
import EyeDropper   from './EyeDropper'

export const CONTAINER_TYPES = [
  { value: 'vial',        label: 'Vial (lyophilized)' },
  { value: 'eyedropper',  label: 'Eye Dropper' },
  { value: 'cream',       label: 'Cream Tube' },
  { value: 'pill',        label: 'Pill Bottle' },
]

const IMAGE_BASE = 'https://cdn.lookslab.shop/assets/products/'

function ContainerVisual({ product, concentration, labelSize, ...rest }) {
  const type = product?.containerType ?? 'vial'
  const name = product?.labelName ?? product?.name ?? ''
  const subtitle = product?.subtitle ?? ''
  const shared = { formulaName: name, formulaSubtitle: subtitle, concentration, labelSize, ...rest }

  switch (type) {
    case 'cream':      return <CreamTube   {...shared} />
    case 'pill':       return <PillBottle  {...shared} />
    case 'eyedropper': return <EyeDropper  {...shared} />
    case 'vial':
    default:           return <PeptideVial {...shared} />
  }
}

/**
 * Picks the right container visual based on product.containerType.
 * Falls back to vial for any unknown / missing type.
 *
 * If product.imageKey is set, a real product photo (served from the CDN) is
 * shown instead of the generated SVG visual. If that image fails to load
 * (e.g. the file hasn't been uploaded to the CDN yet), it falls back to the
 * generated visual rather than leaving a broken-image box in the layout.
 */
export default function ProductVisual({ product, concentration, labelSize, ...rest }) {
  const [imgFailed, setImgFailed] = useState(false)
  useEffect(() => { setImgFailed(false) }, [product?.imageKey])

  if (product?.imageKey && !imgFailed) {
    const name = product?.labelName ?? product?.name ?? ''
    return (
      <img
        src={`${IMAGE_BASE}${encodeURIComponent(product.imageKey)}`}
        alt={`${name}${concentration ? ` ${concentration}` : ''}`}
        /* height stays auto so the photo keeps its own aspect ratio; maxHeight
           lets a container with a definite height (the product card's visual
           box) cap a tall portrait shot instead of letting it grow without
           bound and push the rest of the card off screen. Where the container
           has no set height, a percentage max-height resolves to none, so
           other consumers are unaffected. */
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
        onError={() => setImgFailed(true)}
      />
    )
  }

  return <ContainerVisual product={product} concentration={concentration} labelSize={labelSize} {...rest} />
}
